import { updateAppointment } from "../services/appointmentService";
import { cancelAppointmentReminders } from "../services/appointmentReminderService";

export const APPOINTMENT_AUTO_CANCEL_GRACE_DAYS = 1;

/** @deprecated Use APPOINTMENT_AUTO_CANCEL_GRACE_DAYS */
export const APPOINTMENT_AUTO_CANCEL_GRACE_HOURS = APPOINTMENT_AUTO_CANCEL_GRACE_DAYS;

/**
 * Session-level guard: once we successfully auto-cancel (or attempt) an appointment
 * in this tab, do not PUT/cancel-reminders again. Prevents API loops when the backend
 * still returns the appointment as incomplete, or when the scheduler remounts (HMR / auth).
 */
const sessionAutoCancelAttemptedIds = new Set();

export const getAppointmentStatusUpper = (appointment) =>
  String(
    appointment?.status ||
      appointment?.appointmentStatus ||
      appointment?.appointment_status ||
      ""
  ).toUpperCase();

export const getTokenStatusUpper = (appointment) =>
  String(appointment?.tokenStatus || appointment?.token_status || "").toUpperCase();

export const getPaymentMethodUpper = (appointment) =>
  String(
    appointment?.paymentMethod || appointment?.payment_method || ""
  ).toUpperCase();

export const hasAppointmentToken = (appointment) => {
  const tokenNumber = appointment?.tokenNumber || appointment?.token_number;
  const token = appointment?.token;
  return (
    (tokenNumber != null &&
      tokenNumber !== "" &&
      String(tokenNumber) !== "0" &&
      tokenNumber !== 0) ||
    (token != null && token !== "" && String(token) !== "0" && token !== 0)
  );
};

export const getAppointmentTokenDisplay = (appointment) => {
  // Cancelled appointments keep tokenNumber in DB for sequencing, but do not show it in tables.
  if (isAppointmentCancelled(appointment) || isAppointmentRejected(appointment)) {
    return null;
  }
  if (!hasAppointmentToken(appointment)) return null;
  const tokenNumber = appointment?.tokenNumber ?? appointment?.token_number;
  if (
    tokenNumber != null &&
    tokenNumber !== "" &&
    String(tokenNumber) !== "0" &&
    tokenNumber !== 0
  ) {
    return tokenNumber;
  }
  return appointment?.token ?? null;
};

/** Prefer real token fields only — never fall back to appointment/entity id. */
export const extractTokenNumber = (payload) => {
  if (payload == null) return null;
  const candidates = [
    payload.tokenNumber,
    payload.token_number,
    payload.token,
    payload.number,
  ];
  for (const val of candidates) {
    if (val != null && val !== "" && String(val) !== "0") {
      return val;
    }
  }
  return null;
};

export const getAppointmentDateTimeValue = (appointmentOrDateTime) => {
  if (appointmentOrDateTime == null) return null;
  if (
    typeof appointmentOrDateTime === "string" ||
    appointmentOrDateTime instanceof Date ||
    typeof appointmentOrDateTime === "number"
  ) {
    return appointmentOrDateTime;
  }
  return (
    appointmentOrDateTime.appointmentDateTime ||
    appointmentOrDateTime.appointment_date_time ||
    appointmentOrDateTime.appointmentDate ||
    appointmentOrDateTime.date ||
    null
  );
};

/** True when the appointment calendar day is today (local time). */
export const isAppointmentDateToday = (appointmentOrDateTime, now = new Date()) => {
  const value = getAppointmentDateTimeValue(appointmentOrDateTime);
  if (value == null || value === "") return false;
  const apt = new Date(value);
  if (Number.isNaN(apt.getTime())) return false;
  return (
    apt.getFullYear() === now.getFullYear() &&
    apt.getMonth() === now.getMonth() &&
    apt.getDate() === now.getDate()
  );
};

/**
 * Auto-generate token only for same-day appointments.
 * Tomorrow / upcoming appointments get a token later via Generate Token.
 */
export const shouldAutoGenerateToken = (appointmentOrDateTime, now = new Date()) =>
  isAppointmentDateToday(appointmentOrDateTime, now);

/** Patient cash / pay-at-hospital bookings awaiting front desk confirmation. */
export const needsFrontdeskConfirmation = (appointment) => {
  if (!appointment) return false;
  if (appointment.isCancel === true || appointment.is_cancel === true) {
    return false;
  }

  const status = getAppointmentStatusUpper(appointment);
  if (
    status === "REJECTED" ||
    status === "COMPLETED" ||
    status === "CONFIRMED" ||
    status === "CANCELLED" ||
    status === "CANCELED"
  ) {
    return false;
  }

  if (status === "PENDING_CONFIRMATION" || status === "PENDING") {
    return true;
  }

  const paymentMethod = getPaymentMethodUpper(appointment);
  if (paymentMethod === "PAY_AT_HOSPITAL") {
    return true;
  }

  // Backend may return SCHEDULED instead of PENDING_CONFIRMATION for patient bookings
  if (
    (status === "SCHEDULED" || !status) &&
    paymentMethod === "PAY_AT_HOSPITAL" &&
    !hasAppointmentToken(appointment)
  ) {
    return true;
  }

  return false;
};

const PENDING_CONFIRMATION_ACTION_MSG =
  "Confirm this patient booking from Appointments By Patient first";

/** Disable edit / cancel / delete until front desk confirms patient bookings. */
export const isAppointmentStaffActionDisabled = (appointment) =>
  isAppointmentCancelled(appointment) ||
  isAppointmentCompleted(appointment) ||
  needsFrontdeskConfirmation(appointment);

export const getAppointmentStaffActionDisabledReason = (appointment) => {
  if (isAppointmentCancelled(appointment)) {
    return "Cannot modify a cancelled appointment";
  }
  if (isAppointmentCompleted(appointment)) {
    return "Cannot modify completed appointments";
  }
  if (needsFrontdeskConfirmation(appointment)) {
    return PENDING_CONFIRMATION_ACTION_MSG;
  }
  return "";
};

/** Whether an appointment should appear on the doctor queue (after front desk confirmation). */
export const isVisibleInDoctorQueue = (appointment) => {
  if (!appointment || appointment.isCancel === true || appointment.is_cancel === true) {
    return false;
  }

  const status = getAppointmentStatusUpper(appointment);
  if (status === "REJECTED") return false;
  if (status === "PENDING_CONFIRMATION" || status === "PENDING") return false;

  const paymentMethod = getPaymentMethodUpper(appointment);
  if (paymentMethod === "PAY_AT_HOSPITAL" && status !== "CONFIRMED") {
    return false;
  }

  const hasToken = hasAppointmentToken(appointment);
  const tokenStatus = getTokenStatusUpper(appointment);
  const hasTokenStatus =
    tokenStatus && tokenStatus !== "NULL" && tokenStatus !== "NONE";

  if (status === "CONFIRMED") return true;
  if (status === "SCHEDULED" || status === "ACTIVE") {
    return hasToken || hasTokenStatus;
  }
  if (!status && hasToken) return true;

  return false;
};

export const isAppointmentCancelled = (appointment) => {
  if (!appointment) return false;
  if (appointment.isCancel === true || appointment.is_cancel === true) return true;
  const status = getAppointmentStatusUpper(appointment);
  const tokenStatus = getTokenStatusUpper(appointment);
  return (
    status === "CANCELLED" ||
    status === "CANCELED" ||
    tokenStatus === "CANCELLED" ||
    tokenStatus === "CANCELED"
  );
};

export const getAppointmentDisplayStatus = (appointment) => {
  const status = getAppointmentStatusUpper(appointment);
  if (status === "REJECTED") return "REJECTED";
  if (isAppointmentCancelled(appointment)) return "CANCELLED";
  const tokenStatus = getTokenStatusUpper(appointment);
  if (tokenStatus && tokenStatus !== "NULL" && tokenStatus !== "NONE") {
    return tokenStatus;
  }
  return status || "UNKNOWN";
};

export const isAppointmentCompleted = (appointment) => {
  const tokenStatus = getTokenStatusUpper(appointment);
  const status = getAppointmentStatusUpper(appointment);
  return tokenStatus === "COMPLETED" || status === "COMPLETED";
};

/**
 * True when this appointment should block another booking for the same
 * patient + doctor + day. Completed, cancelled, and rejected visits must not
 * block a new appointment and must not be rescheduled or deleted.
 */
export const isBlockingSameDayAppointment = (appointment) => {
  if (!appointment) return false;
  if (isAppointmentCancelled(appointment) || isAppointmentRejected(appointment)) {
    return false;
  }
  if (isAppointmentCompleted(appointment)) {
    return false;
  }
  return true;
};

/** Show Generate Token when confirmed/scheduled and no token yet. */
export const shouldShowGenerateAppointmentTokenButton = (appointment) => {
  if (!appointment) return false;
  if (isAppointmentCancelled(appointment)) return false;
  if (isAppointmentCompleted(appointment)) return false;
  if (needsFrontdeskConfirmation(appointment)) return false;
  return !hasAppointmentToken(appointment);
};

/** Enable Generate Token only on the appointment's calendar date. */
export const canGenerateAppointmentToken = (appointment, now = new Date()) =>
  shouldShowGenerateAppointmentTokenButton(appointment) &&
  isAppointmentDateToday(appointment, now);

/** frontdesk, admin, super_admin may manually cancel appointments. */
export const canStaffManualCancelRole = (roleName) => {
  const role = String(roleName || "")
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return ["frontdesk", "admin", "super_admin", "superadmin"].includes(role);
};

export const CANCEL_REASON_MANUAL = "Called by patient";
export const CANCEL_REASON_AUTO = "No show";

export const buildCancelAppointmentPayload = (appointment, reason) => ({
  patientId: appointment.patientId || appointment.patient_id,
  doctorId: appointment.doctorId || appointment.doctor_id,
  appointmentDateTime:
    appointment.appointmentDateTime ||
    appointment.appointmentDate ||
    appointment.date,
  status: "CANCELLED",
  tokenStatus: "CANCELLED",
  paymentMethod:
    appointment.paymentMethod || appointment.payment_method || "PAY_AT_HOSPITAL",
  isCancel: true,
  cancelReason: reason || CANCEL_REASON_MANUAL,
});

/** Front desk rejection from Appointments By Patient (keeps REJECTED status). */
export const buildRejectAppointmentPayload = (
  appointment,
  reason = CANCEL_REASON_MANUAL
) => ({
  patientId: appointment.patientId || appointment.patient_id,
  doctorId: appointment.doctorId || appointment.doctor_id,
  appointmentDateTime:
    appointment.appointmentDateTime ||
    appointment.appointmentDate ||
    appointment.date,
  status: "REJECTED",
  tokenStatus: "CANCELLED",
  paymentMethod:
    appointment.paymentMethod || appointment.payment_method || "PAY_AT_HOSPITAL",
  isCancel: true,
  cancelReason: reason || CANCEL_REASON_MANUAL,
});

export const isAppointmentRejected = (appointment) =>
  getAppointmentStatusUpper(appointment) === "REJECTED";

export const shouldShowAppointmentCancelReason = (appointment) =>
  isAppointmentCancelled(appointment) || isAppointmentRejected(appointment);

export const getAppointmentCancelReasonText = (
  appointment,
  fallback = "—"
) => {
  const reason = appointment?.cancelReason || appointment?.cancel_reason;
  if (reason && String(reason).trim()) return String(reason).trim();
  if (shouldShowAppointmentCancelReason(appointment)) {
    return CANCEL_REASON_MANUAL;
  }
  return fallback;
};

export const cancelAppointmentSoft = async (
  appointment,
  { cancelReminders = true, reason = CANCEL_REASON_MANUAL } = {}
) => {
  if (!appointment?.id) {
    throw new Error("Appointment ID is required");
  }
  if (isAppointmentCancelled(appointment)) {
    const err = new Error("This appointment is already cancelled.");
    err.code = "ALREADY_CANCELLED";
    throw err;
  }
  if (isAppointmentCompleted(appointment)) {
    const err = new Error("Cannot cancel a completed appointment.");
    err.code = "ALREADY_COMPLETED";
    throw err;
  }

  await updateAppointment(
    appointment.id,
    buildCancelAppointmentPayload(appointment, reason)
  );

  if (cancelReminders) {
    try {
      await cancelAppointmentReminders(appointment.id);
    } catch (reminderError) {
      console.warn("Failed to cancel appointment reminders:", reminderError);
    }
  }
};

export const isPastAutoCancelThreshold = (
  appointmentDateTime,
  graceDays = APPOINTMENT_AUTO_CANCEL_GRACE_DAYS,
  now = new Date()
) => {
  if (!appointmentDateTime) return false;
  const appointmentTime = new Date(appointmentDateTime);
  if (Number.isNaN(appointmentTime.getTime())) return false;

  const appointmentDayStart = new Date(
    appointmentTime.getFullYear(),
    appointmentTime.getMonth(),
    appointmentTime.getDate()
  );
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const msPerDay = 24 * 60 * 60 * 1000;
  const calendarDaysSinceAppointment = Math.floor(
    (todayStart - appointmentDayStart) / msPerDay
  );

  return calendarDaysSinceAppointment >= graceDays;
};

export const isPastGracePeriod = (
  appointmentDateTime,
  graceDays = APPOINTMENT_AUTO_CANCEL_GRACE_DAYS,
  now = new Date()
) => isPastAutoCancelThreshold(appointmentDateTime, graceDays, now);

export const shouldAutoCancelAppointment = (
  appointment,
  graceDays = APPOINTMENT_AUTO_CANCEL_GRACE_DAYS,
  now = new Date()
) => {
  if (!appointment) return false;
  const appointmentDateTime = getAppointmentDateTimeValue(appointment);
  if (!appointmentDateTime) return false;
  if (isAppointmentCancelled(appointment)) return false;
  if (isAppointmentCompleted(appointment)) return false;
  return isPastAutoCancelThreshold(appointmentDateTime, graceDays, now);
};

/** Auto-cancel past no-show / expired appointments (after appointment day by default). */
export const autoCancelPastAppointments = async (
  appointments,
  { graceDays = APPOINTMENT_AUTO_CANCEL_GRACE_DAYS, now = new Date() } = {}
) => {
  const list = Array.isArray(appointments) ? appointments : [];
  const toCancel = list.filter((a) => {
    if (!a?.id) return false;
    if (sessionAutoCancelAttemptedIds.has(String(a.id))) return false;
    return shouldAutoCancelAppointment(a, graceDays, now);
  });
  if (toCancel.length === 0) {
    return { cancelled: 0, attempted: 0, failures: [] };
  }

  const failures = [];
  let cancelled = 0;

  // Sequential (not Promise.all): avoids flooding the API and starving PatientsTable
  // fetches when many past appointments need cancel + reminder cleanup.
  for (const appointment of toCancel) {
    const idKey = String(appointment.id);
    if (sessionAutoCancelAttemptedIds.has(idKey)) continue;
    // Reserve immediately so overlapping scheduler / page callers skip this id.
    sessionAutoCancelAttemptedIds.add(idKey);

    try {
      await updateAppointment(
        appointment.id,
        buildCancelAppointmentPayload(appointment, CANCEL_REASON_AUTO)
      );
      // Optimistic local mark so later filters in the same run skip this row.
      appointment.isCancel = true;
      appointment.status = "CANCELLED";
      appointment.tokenStatus = "CANCELLED";
      try {
        await cancelAppointmentReminders(appointment.id);
      } catch (reminderError) {
        console.warn(
          "[autoCancelPastAppointments] Failed to cancel reminders:",
          reminderError
        );
      }
      cancelled += 1;
    } catch (error) {
      // Allow a later retry for this id if the status update itself failed.
      sessionAutoCancelAttemptedIds.delete(idKey);
      failures.push({ appointmentId: appointment.id, error });
      console.error(
        `[autoCancelPastAppointments] Failed appointment ${appointment.id}:`,
        error
      );
    }
  }

  if (failures.length && cancelled === 0) {
    throw failures[0]?.error || new Error("Failed to auto-cancel appointments");
  }

  return { cancelled, attempted: toCancel.length, failures };
};
